<template>
  <div>
    <div class="bg-white rounded  my-4 shadow-md px-4 md:px-6 py-2 overflow-hidden mt-3">
      <h2 class="text-gray-800 text-lg font-bold text-center uppercase">Marie Terezie a Josef II. zlepšují život poddaných</h2>
      <div>
        <h3 class="text-blue-600 text-lg font-bold uppercase">Vláda Marie Terezie a Josefa II</h3>
        <p class="pb-2">
          V 18. století se začaly Evropou šířit nové moderní názory na způsob života lidí i na způsob vlády.
          <strong>Lidé se měli řídit svým rozumem </strong> a <strong>vědeckými poznatky</strong>, víc se vzdělávat. Panovníci i šlechta se měli o své poddané co nejlépe starat, zavádět věci pro ně
          prospěšné. <strong>Těmito názory</strong> se na doporučení svých rádců rozhodli řídit dva <strong>habsburští panovníci - Marie Terezie</strong> a její syn <strong>Josef II.</strong>. Rušili
          zastaralé zákony a nařízení. Zavedli <strong>mnoho změn</strong>, které <strong>zlepšily</strong> a <strong>ulehčily život obyčejných lidí</strong>.
        </p>
        <div class="lg:flex  pb-2">
          <div class="w-full  bg-yellow-100">
            <h3 class="text-blue-600 text-lg font-bold uppercase">Jak Marie Terezie zlepšila život prostých lidí</h3>
            <p class=" leading-relaxed">
              <strong>Marie Terezie</strong> nařídila, že <strong>robotovat</strong> smí poddaní <strong>nanejvýš tři dny v týdnu</strong>. Roku 1774 zavedla pětiletou povinnou školní docházku od 6 do
              12 let pro chlapce i děvčata. Děti se učily číst, psát a počítat. Dále se vyučovalo náboženství a praktické dovednosti. Po ukončení 5. třídy byla školní docházka nepovinná. Školy byly
              zřízeny i na vesnicích. Marie Terezie rozdělila zemi na kraje. Každý kraj řídil krajský úřad, jeho nejvyšším úředníkem byl hejtman.
              <span class="my-2 block bg-yellow-300 p-1 text-gray-700 text-sm italic">
                Za vlády Marie Terezie se u nás začaly tisknout první papírové peníze zvané bankocetle. Do té doby se platilo pouze mincemi. Marie Terezie dále zavedla číslování domů. (Dosud se domy
                označovaly pouze pomocí obrazových symbolů - tzv. domovních znamení.) Marie Terezie také vydala nařízení, že lidé musí užívat vedle křestního jména i příjmení.
              </span>
            </p>

            <h3 class="text-blue-600 text-lg font-bold uppercase">Co se změnilo za Josefa II.</h3>
            <div class="lg:flex -mx-2">
              <p class="w-full lg:w-5/6 px-2">
                Josef II., syn Marie Terezie, vydal za dobu své vlády víc než 6000 různých zákonů a nařízení (tehdy se jim říkalo patenty). V roce 1781 zrušil Josef II. v českých zemích nevolnictví.
                Co to znamenalo? Poddaní (rolníci a sedláci) se mohli po zrušení nevlnictví bez svolení svého pána svobodnš stěhovat, ženit a vdávat. Své děti mohli dál vyučit libovolnému řemeslu.
                Nadané děti (i ty chudé) mohly studovat. V církevních školách totiž nemuseli rodiče nic platit. Robota však zrušena nebyla. Poddaní museli i nadále pracovat na polích svého pána a
                odvádět mu daň. Josef II. povolil i jiná náboženství než katolické. Zrušil mnoho klášterů. Nechal jen ty, ve kterých mniši pečovali o nemocné a chudé nebo vyučovali děti. Ve zrušených
                klášterech zřídil nemocnice, ústavy pro chudé a sirotky nebo kasárny pro vojáky. Podporoval řemesla, obchod a podnikání. Za vlády Marie Terezie a zejména potom za vlády Josefa II.
                začaly v českých zemích vznikat velké řemeslné dílny, tzv. manufaktury (z latinského <strong> manu facere</strong> pracovat rukama). Změny zaváděné Marií Terezií a Josefem II. se
                uskutečňovaly postupně a pomalu. Zdaleka ne všichni velkostatkáři a šlechtici byli ochotni je okamžitě přijmout a řídit se jimi.
              </p>
              <img class="w-full lg:w-1/6 px-2" src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/14/Josef_Hickel_Portrait_Josef_II.jpg/515px-Josef_Hickel_Portrait_Josef_II.jpg" alt="" />
            </div>
            <h3 class="text-blue-600 text-lg font-bold uppercase">Kažodenní život venkovských lidí</h3>
            <div class="lg:flex -mx-2 my-2">
              <p class="w-full lg:w-4/6 px-2">
                Na vesnicích bydleli lidé v této době v přízemních staveních. K domu patřil dvůr, chlévy, kurník pro slepice a stodola. Tu se procházelo do zahrady. Největší místností v domě byla
                kuchyně, kde se vařilo, jedlo i spalo. Na polích se pěstovalo hlavně obilí. V této době se u nás začínají pěstovat i brambory.
                <span class="italic">
                  Když obilí uzrálo, požali je rolníci srpy nebo kosamia svázali do snopů. Ty se stavěli do panáků, aby obilí dobře proschlo. Suché obilí svezli do stodoly a tam je vymlátili cepy.
                  Vymlácené zrní prosévali na sítech, aby je vyčistili od plev. (zbytků z obalu zrn). Zrní skladovali v sýpkách.
                </span>
                Venkované jedli velice prostě. Maso měli málokdy. Vařili především luštěniny, kaše, později brambory. Nejčastějším pokrmem během celého dne byla polévka. Pili převážně vodu.
                <span class="italic">
                  Lilek brambor se od konce 16. století šířil v Evropě jako léčivá rostlina nebo pro okrasu. Ve větším množství ho nechala z dnešního Německa přívézt Marie Terezie. Lidé zprvu
                  nevěděli, že lilek brambor má jedlé jen hlízy pod zemí - brambory. Zpočátku jedli zelené bobulky, které se tvoří z květu brambor. Ty jsou však jedovaté, takže se často jejich
                  konzumací otrávili.
                </span>
                <span class="block my-1 font-bold text-lg text-orange-600 text-right">Josef II. vyorává brázdu u Slavíkovic nedaleko Brna.</span>
              </p>

              <img class="w-2/6 px-2" src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/16/Joseph2pflug_1799.jpg/1024px-Joseph2pflug_1799.jpg" alt="" />
            </div>
          </div>
        </div>
        <div class="lg:flex -mx-6 ">
          <div class="w-full lg:w-2/4">
            <iframe
              class="w-full h-64 lg:h-full px-6"
              src="https://www.youtube.com/embed/VUUW-S7VF3w"
              frameborder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>
          <img class="lg:w-2/4 px-6" src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/e7/Mariatheresiaoldfamily.jpg/689px-Mariatheresiaoldfamily.jpg" alt="" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Morava"
};
</script>

<style lang="scss" scoped></style>
